import React, { useEffect, useState } from 'react';
import {
  Box, Paper, Typography, Chip, CircularProgress, Alert,
  Stack, useTheme, alpha, Select, MenuItem, FormControl, InputLabel,
} from '@mui/material';
import {
  Warehouse as WarehouseIcon,
  ChevronRight as OpenIcon,
  Inventory2 as StockIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import {
  getWarehouseOverview, getCompanies, warehouseLabel, warehouseOwnership, companyLabel,
  type WarehouseOverview,
  type WarehouseOverviewRow,
  type ReachableCompany,
} from '../../services/warehouseService';

/**
 * WAREHOUSE-OVERVIEW.1 — what an admin lands on.
 *
 * This replaces five cards reading Wines, Producers, Regions, Grape Varieties
 * and Wine Names. Those were the whole of an admin's first screen while the
 * sidebar, since WINE-DB-NAV.1, deliberately hid every one of them: the wine
 * master data is shared across every client, so editing it is not a per-tenant
 * act. The landing page was the one door that rule never reached, and it was
 * not merely showing what the nav hid — the cards were clickable.
 *
 * WHY WAREHOUSES AND NOT VENUES
 *
 * Venue answers WHO and warehouse answers WHERE, and every stock question is a
 * where-question. A venue with one warehouse sees one row and never notices the
 * distinction, which is the test this had to pass. A company with four venues
 * and a shared cellar sees five rows — not four venues each holding a partial
 * view of the fifth.
 *
 * EVERY ROW SAYS WHY IT IS THERE
 *
 * "Why can I see Alfred's?" is the first question anyone asks of a shared
 * cellar in their list, and a list that cannot answer it trains people to
 * distrust the screen. Yours, or shared with you through these venues.
 */

const fmt = (n: number) => n.toLocaleString(undefined, { maximumFractionDigits: 2 });

const StockHome: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [data, setData] = useState<WarehouseOverview | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [companies, setCompanies] = useState<ReachableCompany[]>([]);
  const [companyId, setCompanyId] = useState<string>('');

  // COMPANY-CONTEXT.1 — a user can hold venues in more than one company. Asked
  // once; the chooser appears only when there is a choice, so a single-company
  // user never meets the concept.
  useEffect(() => {
    getCompanies().then(setCompanies).catch(() => setCompanies([]));
  }, []);

  useEffect(() => {
    setLoading(true);
    getWarehouseOverview(companyId || undefined)
      .then(setData)
      .catch(() => setError('Could not load your warehouses.'))
      .finally(() => setLoading(false));
  }, [companyId]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  const rows = data?.warehouses ?? [];

  return (
    <Box sx={{ p: 3, display: 'flex', flexDirection: 'column', gap: 2.5 }}>
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, flexWrap: 'wrap' }}>
        <Box sx={{ flex: 1, minWidth: 200 }}>
        <Typography variant="h5" fontWeight={700}>Stock</Typography>
        <Typography variant="body2" color="text.secondary">
          {rows.length === 0
            ? 'No warehouses yet.'
            : `${fmt(data!.totals.warehouses)} warehouse${data!.totals.warehouses === 1 ? '' : 's'}`
              + ` · ${fmt(data!.totals.locations)} location${data!.totals.locations === 1 ? '' : 's'}`
              + ` · ${fmt(data!.totals.units)} units on hand`}
        </Typography>
        </Box>

        {/* Only when there IS a choice. One company means no chooser and no
            concept to learn — the same test the warehouse list itself passes
            for a single-warehouse venue. */}
        {companies.length > 1 && (
          <FormControl size="small" sx={{ minWidth: 220 }}>
            <InputLabel>Company</InputLabel>
            <Select
              label="Company"
              value={companyId}
              onChange={(e) => setCompanyId(String(e.target.value))}
            >
              <MenuItem value="">All companies</MenuItem>
              {companies.map(c => (
                <MenuItem key={c.companyId} value={c.companyId}>{companyLabel(c)}</MenuItem>
              ))}
            </Select>
          </FormControl>
        )}
      </Box>

      {error && <Alert severity="error" sx={{ borderRadius: 1 }}>{error}</Alert>}

      {!error && rows.length === 0 && (
        <Paper sx={{ p: 4, borderRadius: 2, textAlign: 'center' }}>
          <StockIcon sx={{ fontSize: 40, color: 'text.disabled', mb: 1 }} />
          <Typography variant="body1" fontWeight={600}>Nothing to show yet</Typography>
          <Typography variant="body2" color="text.secondary">
            You do not have access to any warehouse. If that looks wrong, someone
            with system access can grant your venue access to one.
          </Typography>
        </Paper>
      )}

      <Stack spacing={1.5}>
        {rows.map((w: WarehouseOverviewRow) => {
          return (
            <Paper
              key={w._id}
              onClick={() => navigate(`/admin/stock/${w._id}`)}
              role="link"
              tabIndex={0}
              onKeyDown={(e) => { if (e.key === 'Enter') navigate(`/admin/stock/${w._id}`); }}
              sx={{
                borderRadius: 2,
                cursor: 'pointer',
                boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
                // Yours reads as yours. A borrowed cellar sitting in the same
                // treatment as your own store is what makes the list feel
                // arbitrary.
                borderLeft: `3px solid ${w.isOwn
                  ? theme.palette.primary.main
                  : alpha(theme.palette.text.secondary, 0.3)}`,
                transition: 'box-shadow 0.2s, transform 0.2s',
                '&:hover': { boxShadow: '0 4px 16px rgba(0,0,0,0.10)', transform: 'translateX(2px)' },
                '&:focus-visible': { outline: `2px solid ${theme.palette.primary.main}`, outlineOffset: 2 },
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, px: 2.5, py: 2 }}>
                <WarehouseIcon sx={{ color: 'text.secondary' }} />
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                    {/* WAREHOUSE-LABEL.1 — every venue's home warehouse is
                        auto-named "Main Store", so a company's warehouses were
                        four identical rows. The owning venue is what separates
                        them. */}
                    <Typography variant="subtitle1" fontWeight={600} noWrap>
                      {warehouseLabel(w)}
                    </Typography>
                    <Chip
                      size="small"
                      label={warehouseOwnership(w).chip}
                      color={w.isOwn ? 'primary' : 'default'}
                      variant="outlined"
                    />
                  </Stack>
                  <Typography variant="caption" color="text.secondary">
                    {/* Why this is in YOUR list, when that is not obvious from
                        the name alone. The chip carries the short form. */}
                    {warehouseOwnership(w).detail
                      ? `${warehouseOwnership(w).detail} · `
                      : ''}
                    {w.locationCount} location{w.locationCount === 1 ? '' : 's'}
                    {' · '}{w.itemCount} item{w.itemCount === 1 ? '' : 's'}
                    {' · '}{fmt(w.units)} units
                  </Typography>
                </Box>
                <OpenIcon sx={{ color: 'text.disabled' }} />
              </Box>
            </Paper>
          );
        })}
      </Stack>
    </Box>
  );
};

export default StockHome;
